import {
  BarChart3,
  ClipboardList,
  Database,
  FileText,
  Home,
  Leaf,
  Lightbulb,
  List,
  MessageCircle,
  Package,
  Settings,
  Truck,
} from 'lucide-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { Fragment, Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useMatches, useNavigate, useParams } from 'react-router';

import { AppSidebar } from '@/components/app-sidebar';
import { PageLoadingFallback } from '@/components/PageLoadingFallback';
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ChatSidePanelProvider,
  useChatSidePanel,
} from '@/modules/chat/application/ChatSidePanelProvider';
import { ChatSidePanel } from '@/modules/chat/components/ChatSidePanel';
import { useUser } from '@/modules/common/application/context/user';
import { useChangelogStatus } from '@/modules/common/application/use-changelog-status';
import useCompany from '@/modules/common/application/use-company';
import { useDynamicBreadcrumbs } from '@/modules/common/application/use-dynamic-breadcrumbs';
import { NotificationDialog } from '@/modules/common/components/NotificationDialog/NotificationDialog';
import { AccessScope, FeatureFlag, UserRole } from '@/modules/common/types';
import { useProjectId } from '@/modules/project/hooks/use-projectId';
import { replaceRouteParams } from '@/modules/project/route-util';
import { DocumentDownloadButton } from '@/modules/report/components/DocumentDownload/DocumentDownloadButton';

export type Breadcrumb = {
  label: string;
  to: string;
  active?: boolean;
  dynamicBreadcrumb?: {
    type:
      | 'analyticsDashboard'
      | 'document'
      | 'documentFolder'
      | 'newReport'
      | 'dataPoint'
      | 'product'
      | 'supplier';
    paramKey: string;
  };
};

const createNavigateHandler = (
  navigate: (to: string) => void,
  params: Record<string, string | undefined>
) => {
  return (to: string) => {
    const resolvedPath = replaceRouteParams(to, params);
    navigate(resolvedPath);
  };
};

const useMavue2Label = () => {
  const { t } = useTranslation();

  return (label: string | undefined) => {
    if (!label) return '';

    return t(`modules.mavue2.navigation.${label}`, { defaultValue: label });
  };
};

const DynamicBreadcrumbItem = ({
  breadcrumb,
  paramValue,
  isActive,
  projectId,
  onNavigate,
}: {
  breadcrumb: Breadcrumb;
  paramValue: string;
  isActive: boolean;
  projectId: string;
  onNavigate: (to: string) => void;
}) => {
  const { t } = useTranslation();
  const { getBreadcrumb, isLoading } = useDynamicBreadcrumbs(
    breadcrumb.dynamicBreadcrumb!.type,
    paramValue,
    projectId
  );

  const [labels, setLabels] = useState<
    { id: string | undefined; label: string | undefined; itemType?: string }[]
  >([{ id: breadcrumb.to, label: breadcrumb.label }]);

  useEffect(() => {
    let isMounted = true;
    setLabels([{ id: breadcrumb.to, label: breadcrumb.label }]);

    const loadLabel = async () => {
      try {
        const dynamicLabel = await getBreadcrumb();
        if (dynamicLabel && isMounted) {
          setLabels(
            dynamicLabel.map((item) => ({
              id: item.id,
              label: item.label || '',
              itemType: item.itemType,
            }))
          );
        }
      } catch (error) {
        console.error('Failed to load dynamic breadcrumb:', error);
      }
    };

    loadLabel();

    return () => {
      isMounted = false;
    };
  }, [breadcrumb.label, breadcrumb.to, getBreadcrumb]);

  const activatedLabel = labels.find((label) => label.id === paramValue);
  const otherLabels = labels.filter((label) => label.id !== paramValue);

  const resolveItemPath = (item: { id: string | undefined; itemType?: string }) => {
    const breadcrumbType = breadcrumb.dynamicBreadcrumb!.type;

    if (breadcrumbType === 'document' && item.itemType === 'FOLDER') {
      const basePath = breadcrumb.to.replace(`/:${breadcrumb.dynamicBreadcrumb!.paramKey}`, '');
      return `${basePath}/folder/${item.id}`;
    }

    return breadcrumb.to.replace(`:${breadcrumb.dynamicBreadcrumb!.paramKey}`, item.id || '');
  };

  return (
    <>
      {otherLabels.length === 1 && (
        <>
          <BreadcrumbItem className="hidden md:block whitespace-nowrap">
            <BreadcrumbLink
              onClick={() => onNavigate(resolveItemPath(otherLabels[0]))}
              className="cursor-pointer"
            >
              {otherLabels[0].label}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
        </>
      )}
      {otherLabels.length >= 2 && (
        <>
          <BreadcrumbItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost">
                  <BreadcrumbEllipsis />
                  <span className="sr-only">{t('modules.mavue2.common.toggleMenu')}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuGroup>
                  {otherLabels.map((label) => (
                    <DropdownMenuItem
                      key={label.id ?? label.label}
                      onSelect={() => onNavigate(resolveItemPath(label))}
                    >
                      {label.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
        </>
      )}
      <BreadcrumbItem className="hidden md:block whitespace-nowrap">
        {isActive ? (
          isLoading ? (
            <Skeleton />
          ) : (
            <span className="text-foreground">{activatedLabel?.label}</span>
          )
        ) : isLoading ? (
          <Skeleton />
        ) : (
          <BreadcrumbLink
            onClick={() => {
              if (activatedLabel) {
                onNavigate(resolveItemPath(activatedLabel));
              }
            }}
            className="cursor-pointer"
          >
            {activatedLabel?.label}
          </BreadcrumbLink>
        )}
      </BreadcrumbItem>
    </>
  );
};

// Component to handle static breadcrumb
const StaticBreadcrumbItem = ({
  breadcrumb,
  isActive,
  onNavigate,
}: {
  breadcrumb: Breadcrumb;
  isActive: boolean;
  onNavigate: (to: string) => void;
}) => {
  const getLabel = useMavue2Label();

  return (
    <BreadcrumbItem className="hidden md:block whitespace-nowrap">
      {isActive ? (
        <span className="text-foreground">{getLabel(breadcrumb.label)}</span>
      ) : (
        <BreadcrumbLink onClick={() => onNavigate(breadcrumb.to)} className="cursor-pointer">
          {getLabel(breadcrumb.label)}
        </BreadcrumbLink>
      )}
    </BreadcrumbItem>
  );
};

const NewAppLayoutContent = () => {
  const { t } = useTranslation();
  const matches = useMatches();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { company } = useCompany();
  const { user } = useUser();
  const { hasUnreadChanges } = useChangelogStatus();
  const { projectId } = useProjectId();
  const { openChatPanel, closeChatPanel } = useChatSidePanel();
  const isProjectEnabled = useFeatureFlagEnabled(FeatureFlag.PROJECTS);
  const isAnalyticsEnabled = useFeatureFlagEnabled(FeatureFlag.ANALYTICS) === true;

  // Create navigation handler with parameter replacement
  const handleNavigate = createNavigateHandler(navigate, params);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);

  const rawBreadcrumbs = useMemo(
    () =>
      matches
        .filter(
          (match): match is { handle: { breadcrumb: Breadcrumb[] } } & typeof match =>
            match.handle !== null &&
            typeof match.handle === 'object' &&
            'breadcrumb' in match.handle
        )
        .flatMap((match) => match.handle.breadcrumb),
    [matches]
  );

  const companyPlan: 'unlimited' | 'materiality' =
    company?.accessScope === AccessScope.UNLIMITED ? 'unlimited' : 'materiality';
  const companyData = [
    {
      name: company?.name ?? t('modules.mavue2.navigation.company'),
      logo: BarChart3,
      plan: companyPlan,
    },
  ];

  const userData = {
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    email: user?.email ?? 'user@example.com',
    avatar: 'https://via.placeholder.com/150',
  };

  const isExternalUser = user?.role === UserRole.EXTERNAL;
  const isChatRoute = location.pathname === '/chat' || location.pathname.startsWith('/chat/');
  const showChatSwitcher = !isExternalUser;

  useEffect(() => {
    if (isChatRoute) {
      closeChatPanel();
    }
  }, [closeChatPanel, isChatRoute]);

  const handleSelectHome = () => {
    if (location.pathname === '/home') {
      return;
    }

    navigate('/home');
  };

  const handleSelectChat = () => {
    if (isChatRoute) {
      return;
    }

    navigate('/chat');
  };

  const handleOpenChatPanel = () => {
    openChatPanel({
      url: `${location.pathname}${location.search}`,
    });
  };

  const coreNavItems = [
    {
      title: t('modules.mavue2.navigation.home'),
      url: '/home',
      icon: Home,
      isActive: false,
      items: [
        {
          title: t('modules.mavue2.navigation.home'),
          url: '/home',
        },
      ],
      hideForExternal: true,
    },
    {
      title: t('modules.mavue2.navigation.reports'),
      url: '/reporting',
      icon: ClipboardList,
      isActive: false,
      items: [
        {
          title: t('modules.mavue2.navigation.reports'),
          url: '/reporting',
        },
      ],
      hideForExternal: false,
    },
    ...(isAnalyticsEnabled
      ? [
          {
            title: t('modules.mavue2.navigation.analytics'),
            url: '/analytics',
            icon: BarChart3,
            isActive: false,
            items: [
              {
                title: t('modules.mavue2.navigation.analytics'),
                url: '/analytics',
              },
            ],
            hideForExternal: true,
          },
        ]
      : []),
    {
      title: t('modules.mavue2.navigation.datapoints'),
      url: '/custom-data',
      icon: Database,
      isActive: false,
      items: [
        {
          title: t('modules.mavue2.navigation.datapoints'),
          url: '/custom-data',
        },
      ],
      hideForExternal: true,
    },
    {
      title: t('modules.mavue2.navigation.documents'),
      url: '/documents',
      icon: FileText,
      isActive: false,
      items: [
        {
          title: t('modules.mavue2.navigation.documents'),
          url: '/documents',
        },
      ],
      hideForExternal: false,
    },
    {
      title: t('modules.mavue2.navigation.tasks'),
      url: '/tasks',
      icon: List,
      isActive: false,
      items: [
        {
          title: t('modules.mavue2.navigation.tasks'),
          url: '/tasks',
        },
      ],
      hideForExternal: true,
    },
    {
      title: t('modules.mavue2.navigation.settings'),
      url: '/app/settings',
      icon: Settings,
      isActive: false,
      items: [
        {
          title: t('modules.mavue2.navigation.settings'),
          url: '/app/settings',
        },
      ],
      hideForExternal: false,
    },
  ];

  const masterDataNavItems = [
    {
      title: t('modules.mavue2.navigation.products'),
      url: '/products',
      icon: Package,
      isActive: false,
      items: [
        {
          title: t('modules.mavue2.navigation.products'),
          url: '/products',
        },
      ],
      hideForExternal: true,
    },
    {
      title: t('modules.mavue2.navigation.suppliers'),
      url: '/suppliers',
      icon: Truck,
      isActive: false,
      items: [
        {
          title: t('modules.mavue2.navigation.suppliers'),
          url: '/suppliers',
        },
      ],
      hideForExternal: true,
    },
    {
      title: t('modules.mavue2.navigation.emissionFactors'),
      url: '/emission-factors',
      icon: Leaf,
      isActive: false,
      items: [
        {
          title: t('modules.mavue2.navigation.emissionFactors'),
          url: '/emission-factors',
        },
      ],
      hideForExternal: true,
    },
  ];

  const platformNavItems = coreNavItems;

  const filterNavItems = <T extends { hideForExternal?: boolean }>(items: T[]) => {
    if (!isExternalUser) {
      return items;
    }

    return items.filter((item) => !item.hideForExternal);
  };

  const navSections = [
    {
      label: t('modules.mavue2.navigation.platform'),
      items: filterNavItems(platformNavItems),
    },
    {
      label: t('modules.mavue2.navigation.masterData'),
      items: filterNavItems(masterDataNavItems),
    },
  ].filter((section) => section.items.length > 0);

  const showPreviewButton = location.pathname.match(
    /^(?:\/project\/[^/]+)?\/reporting\/[^/]+(?:\/[^/]+)*$/
  );

  if (!projectId) {
    return <PageLoadingFallback className="h-screen" />;
  }

  return (
    <div className="h-screen flex flex-col min-w-0">
      <SidebarProvider>
        <div className="flex flex-1 overflow-hidden min-w-0">
          <AppSidebar
            user={userData}
            teams={companyData}
            navSections={navSections}
            isProjectEnabled={isProjectEnabled ?? false}
            userRole={user?.role}
            showChatSwitcher={showChatSwitcher}
            sidebarMode={isChatRoute ? 'chat' : 'home'}
            onSelectHome={handleSelectHome}
            onSelectChat={handleSelectChat}
          />
          <SidebarInset className="flex flex-col flex-1 min-w-0">
            <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12 min-w-0">
              <div className="flex items-center gap-2 px-4 flex-1 min-w-0 overflow-hidden">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <SidebarTrigger className="-ml-1" />
                  <Separator orientation="vertical" className="mr-2 h-4" />
                </div>
                <Breadcrumb className="min-w-0 flex-1 overflow-hidden">
                  <BreadcrumbList className="flex-nowrap overflow-x-auto scrollbar-hide">
                    {rawBreadcrumbs.map((breadcrumb, index) => {
                      const isActive = breadcrumb.active ?? false;
                      const hasDynamicBreadcrumb = breadcrumb.dynamicBreadcrumb;
                      const paramValue = hasDynamicBreadcrumb
                        ? params[breadcrumb.dynamicBreadcrumb!.paramKey]
                        : null;
                      return (
                        <Fragment key={breadcrumb.to}>
                          {index > 0 ? (
                            <BreadcrumbSeparator className="hidden md:block flex-shrink-0" />
                          ) : null}

                          {hasDynamicBreadcrumb && paramValue ? (
                            <DynamicBreadcrumbItem
                              breadcrumb={breadcrumb}
                              paramValue={paramValue}
                              isActive={isActive}
                              onNavigate={handleNavigate}
                              projectId={projectId}
                            />
                          ) : (
                            <StaticBreadcrumbItem
                              breadcrumb={breadcrumb}
                              isActive={isActive}
                              onNavigate={handleNavigate}
                            />
                          )}
                        </Fragment>
                      );
                    })}
                  </BreadcrumbList>
                </Breadcrumb>
              </div>

              <div className="flex items-center gap-2 px-4 flex-shrink-0">
                {showChatSwitcher && !isChatRoute && (
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label={t('modules.mavue2.chat.openSidePanel')}
                    onClick={handleOpenChatPanel}
                  >
                    <MessageCircle className="w-4 h-4" />
                  </Button>
                )}

                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setNotificationDialogOpen(true)}
                  >
                    <Lightbulb className="w-4 h-4" />
                  </Button>
                  {hasUnreadChanges && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full border-2 border-background"></div>
                  )}
                </div>

                {showPreviewButton && <DocumentDownloadButton projectId={projectId} />}
              </div>
            </header>
            <Separator />
            <div className="flex min-h-0 flex-1 overflow-hidden w-full">
              <div className="flex-1 overflow-auto w-full min-w-0">
                <Suspense fallback={<PageLoadingFallback />}>
                  <Outlet />
                </Suspense>
              </div>
              {!isChatRoute && <ChatSidePanel />}
            </div>
          </SidebarInset>
        </div>
      </SidebarProvider>
      <NotificationDialog open={notificationDialogOpen} onOpenChange={setNotificationDialogOpen} />
    </div>
  );
};

const NewAppLayout = () => (
  <ChatSidePanelProvider>
    <NewAppLayoutContent />
  </ChatSidePanelProvider>
);

export default NewAppLayout;
